import { Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { AppService } from '@services/app.service';

interface PlantMarker {
  lat: number;
  lng: number;
  stationName: string;
  stationCode: string;
  [key: string]: any;
}

@Component({
  selector: 'app-dashboard-map',
  templateUrl: './dashboard-map.component.html',
  styleUrls: ['./dashboard-map.component.scss']
})
export class DashboardMapComponent implements OnInit {

  center: google.maps.LatLngLiteral = {
    lat: 30.33084,
    lng: 71.247499
  };
  zoom = 6;

  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    fullscreenControl: true,
    clickableIcons: false,
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
      { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
      { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
      { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9e6ff' }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }
    ]
  };

  markerPositions: PlantMarker[] = [];
  filteredMarkers: PlantMarker[] = [];
  searchTerm = '';
  activeMarker: PlantMarker | null = null;
  loading = false;

  @ViewChild(GoogleMap) googleMap: GoogleMap | undefined;
  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow | undefined;
  @ViewChildren(MapMarker) mapMarkerRefs: QueryList<MapMarker> | undefined;

  constructor(private appService: AppService) { }

  ngOnInit(): void {
    this.fetchPlantLocations();
  }

  fetchPlantLocations(): void {
    this.loading = true;
    this.appService.GetPlantLocation().subscribe({
      next: (res) => {
        this.markerPositions = (res?.data || []).map((item: any) => ({
          ...item,
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lng)
        }));
        this.filteredMarkers = this.markerPositions;
        this.loading = false;
        setTimeout(() => this.fitBoundsToMarkers());
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  fitBoundsToMarkers(): void {
    if (!this.googleMap || typeof google === 'undefined' || this.markerPositions.length === 0) {
      return;
    }
    if (this.markerPositions.length === 1) {
      this.center = { lat: this.markerPositions[0].lat, lng: this.markerPositions[0].lng };
      this.zoom = 11;
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    this.markerPositions.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
    this.googleMap.fitBounds(bounds, 40);
  }

  onSearchChange(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredMarkers = !term
      ? this.markerPositions
      : this.markerPositions.filter(m =>
        (m.stationName || '').toLowerCase().includes(term) ||
        (m.stationCode || '').toLowerCase().includes(term)
      );
  }

  private normalIcon: google.maps.Icon | undefined;
  private activeIcon: google.maps.Icon | undefined;

  private buildIcon(fill: string, size: number): google.maps.Icon {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24C32 7.163 24.837 0 16 0z" fill="${fill}"/>
      <circle cx="16" cy="15" r="6.5" fill="#ffffff"/>
    </svg>`;
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(size, size * 1.25),
      anchor: new google.maps.Point(size / 2, size * 1.25)
    };
  }

  // Returns a cached, stable icon reference per state so the map-marker
  // directive doesn't see a "new" icon (and re-render/flicker the pin)
  // on every change-detection tick.
  markerIcon(plant: PlantMarker): google.maps.Icon | undefined {
    if (typeof google === 'undefined') {
      return undefined;
    }
    if (!this.normalIcon) {
      this.normalIcon = this.buildIcon('#ff8c42', 32);
    }
    if (!this.activeIcon) {
      this.activeIcon = this.buildIcon('#2D2D2D', 38);
    }
    return this.activeMarker === plant ? this.activeIcon : this.normalIcon;
  }

  selectPlant(plant: PlantMarker, index: number): void {
    this.activeMarker = plant;
    if (this.googleMap) {
      this.googleMap.panTo({ lat: plant.lat, lng: plant.lng });
    }
    this.zoom = Math.max(this.zoom, 12);

    const markerRef = this.mapMarkerRefs?.toArray()[index];
    if (markerRef && this.infoWindow) {
      this.infoWindow.open(markerRef);
    }
  }

  onMarkerClick(plant: PlantMarker, index: number): void {
    this.selectPlant(plant, index);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.onSearchChange();
  }

  trackByStationCode(index: number, plant: PlantMarker): string {
    return plant.stationCode;
  }
}
